import { observer } from 'mobx-react-lite'
import { useRef } from 'react'
import {
  CalendarDropdown,
  FullPageWrapper,
  Typography,
  SpinnerLoader,
  type ActionItem,
  Button,
} from 'shared/ui'
import { useElementWidth } from 'shared/hooks'
import { BreakdownFilter } from 'pages/analytics/ui/BreakdownFilter/BreakdownFilter'
import {
  ConversationsAnalyticsPageProvider,
  useConversationsAnalyticsPageContext,
} from 'pages/analytics/pages/conversations/context/conversationsAnalyticsPageContext'
import { AnalyticsHeader, TrendLabel, AnalyticsEmptyState } from 'pages/analytics/ui'
import { ChartFilter } from 'pages/analytics/ui/ChartFilter'
import {
  ConversationsLineChart,
  ConversationsBarChart,
} from 'pages/analytics/pages/conversations/ui/Chart'
import { ConversationsComparisonTable } from 'pages/analytics/pages/conversations/ui/ComparisonTable'
import styles from './styles.module.scss'

const ConversationsAnalyticsPageContent = observer(() => {
  const {
    calendarFilterStore,
    chartFilterStore,
    chartDataStore,
    pageLoading,
    chartLoading,
    openExportModal,
    breakdownFilterStore,
    comparisonTableStore,
  } = useConversationsAnalyticsPageContext()
  const { clearAllFilters, hasAppliedFilters } = breakdownFilterStore
  const { getStatisticByKey, hasStatistic } = chartDataStore

  const filtersRowRef = useRef<HTMLDivElement | null>(null)
  const filtersRowWidth = useElementWidth(filtersRowRef)

  const totalStat = getStatisticByKey('total')
  const openStat = getStatisticByKey('open')
  const closedStat = getStatisticByKey('closed')

  const exportActions: ActionItem[] = [
    {
      text: 'Export data',
      icon: 'download',
      onAction: openExportModal,
    },
  ]

  const renderContent = () => {
    if (pageLoading) {
      return (
        <div className={styles.fullPageContainer}>
          <SpinnerLoader ariaLabel={'ConversationsAnalyticsPage'} />
        </div>
      )
    }

    if (!hasStatistic) {
      return (
        <div className={styles.fullPageContainer}>
          <AnalyticsEmptyState />
        </div>
      )
    }

    return (
      <>
        <div className={styles.kpiSection}>
          <div className={styles.kpiItem}>
            <Typography variant={'body-rg-regular'} color={'tertiary'} ariaLabel={'TotalTitle'}>
              Total conversations
            </Typography>
            <Typography variant={'heading-lg'} color={'primary'} ariaLabel={'TotalValue'}>
              {getStatisticByKey('total')?.value?.toLocaleString() || '0'}
            </Typography>
            {totalStat?.trend && <TrendLabel trend={totalStat.trend} displayMode='percent' />}
          </div>
          <div className={styles.kpiDivider}></div>
          <div className={styles.kpiItem}>
            <Typography variant={'body-rg-regular'} color={'tertiary'} ariaLabel={'OpenTitle'}>
              Open
            </Typography>
            <Typography variant={'heading-lg'} color={'primary'} ariaLabel={'OpenValue'}>
              {getStatisticByKey('open')?.value?.toLocaleString() || '0'}
            </Typography>
            {openStat?.trend && <TrendLabel trend={openStat.trend} />}
          </div>
          <div className={styles.kpiDivider}></div>
          <div className={styles.kpiItem}>
            <Typography variant={'body-rg-regular'} color={'tertiary'} ariaLabel={'ClosedTitle'}>
              Closed
            </Typography>
            <Typography variant={'heading-lg'} color={'primary'} ariaLabel={'ClosedValue'}>
              {getStatisticByKey('closed')?.value?.toLocaleString() || '0'}
            </Typography>
            {closedStat?.trend && <TrendLabel trend={closedStat.trend} />}
          </div>
        </div>

        <div className={styles.chartSection}>
          <div className={styles.chartHeader}>
            <Typography variant='heading-sm' ariaLabel='NewConversationsTitle'>
              New conversations
            </Typography>
            <ChartFilter store={chartFilterStore} />
          </div>

          <div className={styles.chartArea}>
            {chartLoading ? (
              <SpinnerLoader ariaLabel={'ConversationsAnalyticsPage'} />
            ) : (
              <>
                {chartFilterStore.chartType === 'line' ? (
                  <ConversationsLineChart chartDataStore={chartDataStore} />
                ) : (
                  <ConversationsBarChart chartDataStore={chartDataStore} />
                )}
              </>
            )}
          </div>
        </div>

        {hasAppliedFilters && (
          <div className={styles.tableSection}>
            <ConversationsComparisonTable store={comparisonTableStore} />
          </div>
        )}
      </>
    )
  }

  return (
    <FullPageWrapper className={styles.page}>
      <AnalyticsHeader title='Conversations' actions={exportActions} />

      <div className={styles.filtersRow} ref={filtersRowRef}>
        <CalendarDropdown store={calendarFilterStore} />
        <BreakdownFilter store={breakdownFilterStore} parentWidth={filtersRowWidth} />
        {hasAppliedFilters && (
          <Button
            contained='secondary'
            color={'var(--content-primary-secondary)'}
            typeBtn='text'
            text='Clear all'
            onClick={clearAllFilters}
          />
        )}
      </div>

      {renderContent()}
    </FullPageWrapper>
  )
})

export const ConversationsAnalyticsPage = () => (
  <ConversationsAnalyticsPageProvider>
    <ConversationsAnalyticsPageContent />
  </ConversationsAnalyticsPageProvider>
)
